import Datetime from "components/shared/datetime";
import { Select } from "components/shared/form";
import debounce from "lodash/debounce";
import React, { FC, useCallback, useState } from "react";
import { FilterValue, Row } from "react-table";

interface TableColumn {
    id: string;
    filterType: string;
    filterValue: { value: string | number };
    setFilter: (props: { value: number | string }) => void;
    filterable: boolean;
    isMulti: boolean;
}

export interface FilterProps {
    filtered: { [key: string]: { value?: FilterValue } };
    onFilteredChange: (props: {
        id: string;
        value: FilterValue;
        filterType: string;
        isMulti?: boolean;
        option?: { name: string; value: string | number; option: {} };
        preparedFilter?: {};
    }) => void;
    column: TableColumn;
    filterEnum?: { label: string; value: string | number }[];
    selectProps?: {};
    defaultValue?: {};
    options?: { label: string; value: string | number }[];
    libOptions?: { className?: string; [key: string]: unknown };
    dateInputType?: string;
}

export { getVisiblePages } from "./pagination";

export const DefaultColumnFilter = ({ filtered, onFilteredChange, column }: FilterProps) => {
    return (
        <input
            type="text"
            className="field-wrap__input"
            name={column.id}
            onChange={(e) => onFilteredChange({ id: column.id!, value: e.target.value, filterType: column.filterType })}
            value={filtered[column.id] ? filtered[column.id].value : ""}
        />
    );
};

export const DebouncedDefaultColumnFilter = withDebounce(DefaultColumnFilter);

export const rowIndexColumn = {
    Header: "#",
    id: "row",
    accessor: (d: { index: string }) => d.index,
    Cell: ({ row, page, pageSize }: { row: Row; page: number; pageSize: number }) => page * pageSize + row.index + 1,
    width: 50,
    disableSortBy: false,
    sortable: false,
    notExportable: true,
};

export const isDeletedColumn = (restore: (id: string) => void, canEditBooks: boolean) => ({
    Header: "Удален",
    accessor: "isDeleted",
    sortable: true,
    filterable: true,
    filterType: "boolean",
    Filter: (props: FilterProps) => {
        const defaultValue = { id: "isDeleted", value: "false", filterType: "boolean" };
        const options = [
            { label: "Показать все", value: "all" },
            { label: "Удаленные", value: "true" },
            { label: "Не удаленные", value: "false" },
        ];
        return <BooleanColumnFilter {...props} options={options} defaultValue={defaultValue} />;
    },
    Cell: ({
        value,
        row: {
            original: { id },
        },
    }: {
        value: string;
        row: Row<{ id: string }>;
    }) => (
        <div className="text-center">
            {value ? "Да" : "Нет"}
            {value && canEditBooks && (
                <span className="btn btn-xs btn-brand ml-2" onClick={() => restore(id)}>
                    Восстановить
                </span>
            )}
        </div>
    ),
    CSVExport: ({ value }: { value: boolean }) => (value ? "Да" : "Нет"),
});

export function reportFilterWrapper(WrappedComponent: FC<FilterProps>) {
    return function ({ column, ...props }: { column: TableColumn }) {
        const { filterValue, setFilter, filterable } = column;
        if (!filterable || !setFilter) {
            return null;
        }
        const [value, setValue] = useState(filterValue ? filterValue.value : "");
        const debouncedChange = useCallback(debounce(setFilter, 500), []);
        const onFilterChange = (props: { value: FilterValue }) => {
            setValue(props.value);
            debouncedChange(props);
        };
        return (
            <WrappedComponent
                onFilteredChange={onFilterChange}
                filtered={{ [column.id]: { value } }}
                column={column}
                {...props}
            />
        );
    };
}

function withDebounce(WrappedComponent: FC<FilterProps>) {
    return function ({ onFilteredChange, filtered, column, ...props }: FilterProps) {
        const [value, setValue] = useState(filtered[column.id] ? filtered[column.id].value : "");
        const debouncedChange = useCallback(debounce(onFilteredChange, 500), []);
        const onFilterChange = (props: {
            id: string;
            value: string;
            filterType: string;
            isMulti?: boolean;
            option?: { name: string; value: string | number; option: {} };
        }) => {
            setValue(props.value);
            debouncedChange(props);
        };
        return (
            <WrappedComponent
                onFilteredChange={onFilterChange}
                filtered={{ ...filtered, [column.id]: { ...filtered[column.id], value } }}
                column={column}
                {...props}
            />
        );
    };
}

export const EnumColumnFilter = ({ filtered, onFilteredChange, filterEnum, column }: FilterProps) => (
    <select
        className="r-item__filter-select r-item__filter-select--wide field-wrap__input"
        onChange={(e) => onFilteredChange({ id: column.id, value: e.target.value, filterType: column.filterType })}
        value={filtered[column.id] ? filtered[column.id].value : "all"}
        name={column.id}
    >
        <option value="all">Показать все</option>
        {(filterEnum || []).map((option) => (
            <option key={option.value} value={option.value}>
                {option.label}
            </option>
        ))}
    </select>
);

export const SelectColumnFilter = ({ filtered, onFilteredChange, selectProps, column }: FilterProps) => {
    const props = {
        ...selectProps,
        wrapper: "book-field",
        onChange: (option: {
            name: string;
            value: string | number;
            option: { name: string; value: string | number; option: {} };
        }) =>
            onFilteredChange({
                id: column.id,
                value: option.value,
                filterType: column.filterType,
                isMulti: column.isMulti,
                option: option.option,
            }),
        value: filtered[column.id] ? filtered[column.id].value : null,
        menuPortalTarget: document.body,
        bWrapper: "row",
        styles: {
            menu: (styles: {}) => ({ ...styles, minWidth: "250px" }),
            container: (styles: {}) => ({ ...styles, width: "100%" }),
            control: (styles: {}) => ({ ...styles, minHeight: "26px", fontSize: "14px" }),
            input: (styles: {}) => ({ ...styles, height: "17px", paddingTop: 0, marginTop: 0, fontWeight: 400 }),
            singleValue: (styles: {}) => ({ ...styles, height: "18px", lineHeight: "18px", fontWeight: 400 }),
            multiValue: (styles: {}) => ({ ...styles, height: "18px", lineHeight: "14px" }),
            valueContainer: (styles: {}) => ({ ...styles, lineHeight: "18px", flexWrap: "nowrap" }),
            indicatorsContainer: (styles: {}) => ({ ...styles, height: "26px" }),
            clearIndicator: (styles: {}) => ({ ...styles, padding: "4px" }),
            indicatorSeparator: () => ({ display: "none" }),
            option: (styles: {}) => ({ ...styles, fontSize: "14px" }),
            placeholder: (styles: {}) => ({ ...styles, lineHeight: "18px" }),
        },
    };
    return <Select {...props} />;
};

export const BooleanColumnFilter = ({
    filtered,
    onFilteredChange,
    column,
    defaultValue = {},
    options = [
        { label: "Показать все", value: "all" },
        { label: "Отмеченные", value: "true" },
        { label: "Не отмеченные", value: "false" },
    ],
}: FilterProps) => {
    const filterVal = filtered || defaultValue;
    return (
        <select
            className="r-item__filter-select r-item__filter-select--wide field-wrap__input"
            onChange={(e) => onFilteredChange({ id: column.id, value: e.target.value, filterType: column.filterType })}
            value={filterVal[column.id] ? filterVal[column.id].value : "all"}
            name={column.id}
        >
            {options.map(({ value, label }) => (
                <option key={value} value={value}>
                    {label}
                </option>
            ))}
        </select>
    );
};

export const DateColumnFilter = ({
    filtered,
    onFilteredChange,
    column,
    libOptions = {},
    dateInputType,
}: FilterProps) => {
    const value = (
        filtered[column.id] && filtered[column.id].value ? filtered[column.id].value : { key: "eq", value: "" }
    ) as {
        key: string;
        value: string;
    };
    const dateLibOptions = { ...libOptions, className: "field-wrap__input" };
    const inputProps = dateInputType ? { name: column.id, type: dateInputType } : { name: column.id };

    return (
        <>
            <select
                className="r-item__filter-select field-wrap__input"
                onChange={({ target }) =>
                    onFilteredChange({
                        id: column.id,
                        value: { ...value, key: target.value } as {
                            key: string;
                            value: string;
                        },
                        filterType: column.filterType,
                    })
                }
                value={value!.key}
                name={`${column.id}Select`}
            >
                <option value="eq">Равно</option>
                <option value="ge">Больше</option>
                <option value="le">Меньше</option>
            </select>
            <div className="r-item__filter-date-wrap">
                <Datetime
                    inPortal
                    value={value.value}
                    inputProps={inputProps}
                    onChange={(inputValue: { value: string }) =>
                        onFilteredChange({
                            id: column.id,
                            value: { ...value, value: inputValue.value },
                            filterType: column.filterType,
                        })
                    }
                    libOptions={dateLibOptions}
                />
            </div>
        </>
    );
};

export const NumericColumnFilter = ({ filtered, onFilteredChange, column }: FilterProps) => {
    const value =
        filtered[column.id] && filtered[column.id].value ? filtered[column.id].value : { key: "eq", value: "" };
    return (
        <>
            <select
                className="r-item__filter-select field-wrap__input"
                onChange={({ target }) =>
                    onFilteredChange({
                        id: column.id,
                        value: { ...value, key: target.value },
                        filterType: column.filterType,
                    })
                }
                value={value.key}
                name={`${column.id}Select`}
            >
                <option value="eq">Равно</option>
                <option value="ge">Больше</option>
                <option value="le">Меньше</option>
            </select>
            <input
                className=" r-item__filter-input field-wrap__input"
                value={value.value}
                name={column.id}
                onChange={({ target }) =>
                    onFilteredChange({
                        id: column.id,
                        value: { ...value, value: target.value },
                        filterType: column.filterType,
                    })
                }
            />
        </>
    );
};

const RangeColumnFilter = ({ filtered, onFilteredChange, column }: FilterProps) => {
    const value = filtered[column.id] && filtered[column.id].value ? filtered[column.id].value : { start: "", end: "" };

    return (
        <div className="d-flex">
            <input
                value={value.start ?? ""}
                className="field-wrap__input px-1"
                placeholder="От"
                type="number"
                name={`${column.id}-from`}
                onChange={({ target }) =>
                    onFilteredChange({
                        id: column.id,
                        value: { ...value, start: target.value ? Number(target.value) : null },
                        filterType: column.filterType,
                    })
                }
            />
            <span className="px-1">&#8211;</span>
            <input
                value={value.end ?? ""}
                className="field-wrap__input px-1"
                placeholder="До"
                type="number"
                name={`${column.id}-to`}
                onChange={({ target }) =>
                    onFilteredChange({
                        id: column.id,
                        value: { ...value, end: target.value ? Number(target.value) : null },
                        filterType: column.filterType,
                    })
                }
            />
        </div>
    );
};

export const DateRangeColumnFilter = ({ filtered, onFilteredChange, column, libOptions = {} }: FilterValue) => {
    const value = filtered[column.id] && filtered[column.id].value ? filtered[column.id].value : { start: "", end: "" };
    libOptions.className = "field-wrap__input";
    const inputPropsRest = column.filterType === "dateRange" ? { type: "date" } : {};

    return (
        <div className="d-flex">
            <Datetime
                inPortal
                placeholder="От"
                value={value.start}
                inputProps={{ name: column.id, ...inputPropsRest }}
                onChange={(inputValue: { value: string }) =>
                    inputValue.value !== value.start &&
                    onFilteredChange({
                        id: column.id,
                        value: { ...value, start: inputValue.value },
                        filterType: column.filterType,
                    })
                }
                libOptions={libOptions}
            />
            <span className="px-1">&#8211;</span>
            <Datetime
                inPortal
                placeholder="До"
                value={value.end}
                inputProps={{ name: column.id, ...inputPropsRest }}
                onChange={(inputValue: { value: string }) =>
                    inputValue.value !== value.end &&
                    onFilteredChange({
                        id: column.id,
                        value: { ...value, end: inputValue.value },
                        filterType: column.filterType,
                    })
                }
                libOptions={libOptions}
            />
        </div>
    );
};

export const DebouncedRangeColumnFilter = withDebounce(RangeColumnFilter);
